import type { ShippingAddressInput } from '@graphql/generated-base';
import { isAddressValid, isNonContinentalUSA } from '@apps/registry/common/utils/shippingAddressValidations';

const USA_COUNTRY_CODE = 'USA';
const USA_COUNTRY_NAME = 'United States';

/**
 * The address fields canShowGW reads. Shaped to accept both a ShippingAddressFragment (the
 * registry's configured address) and a ShippingAddressInput (the feature-flag payload) without
 * either needing to be narrowed first. Every field is optional because GraphQL nullability differs
 * between those two types, and callers legitimately hold neither.
 */
type GiftWrapAddress = Readonly<{
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
  name?: string | null;
  phone?: string | null;
  postalCode?: string | null;
  state?: string | null;
  validated?: boolean | null;
}>;

// Strip extra GraphQL response fields (e.g. __typename, hash) not accepted by ShippingAddressInput.
const toShippingAddressInput = (address: ShippingAddressInput): ShippingAddressInput => {
  const { address1, address2, city, country, countryCode, name, phone, postalCode, state, validated } = address;
  return { address1, address2, city, country, countryCode, name, phone, postalCode, state, validated };
};

// countryCode is null when the country was entered as freeform text, so mirror isUSAddress and read
// 'United States' as USA. US-only: no other freeform country name gives us a code we can trust.
const getCountryCode = (address: GiftWrapAddress | null | undefined): string | undefined =>
  address?.countryCode?.trim() || (address?.country?.trim() === USA_COUNTRY_NAME ? USA_COUNTRY_CODE : undefined);

// registry_service does not ship Gift Wrap outside the continental US, so an Alaska/Hawaii/territory
// address must not be offered Gift Wrap even though its country is otherwise supported. Offering it
// and failing at purchase time is worse UX than never offering it.
const isUnshippableUSAddress = (countryCode: string, address: GiftWrapAddress | null | undefined): boolean => {
  if (countryCode.toUpperCase() !== USA_COUNTRY_CODE) {
    return false;
  }

  const state = address?.state?.trim();
  return !!state && isNonContinentalUSA(state);
};

export enum EAddressType {
  US_REGISTRY = 'US_registry',
  US_DEFAULT = 'US_default',
  INTERNATIONAL_REGISTRY = 'International_registry',
  US_NON_CONTINENTAL = 'US_non_continental',
  NO_ADDRESS = 'NoAddress',
}

interface canShowGWResponse {
  // Either a valid Address or undefined
  address: ShippingAddressInput | undefined;
  // If Gift Wrap offer should be shown
  allow: boolean;
  // Type of address selected
  type: EAddressType;
}

/**
 * A country code is enough to offer Gift Wrap, so the registry's configured shipping address decides
 * it, falling back to the address configured in the registryGiftWrapDefaultUSABillingAddress flag.
 *
 * The event location is deliberately not consulted: `EventLocation` exposes no `countryCode` (see
 * src/graphql/schema.graphql), it describes a venue rather than a billing destination, and
 * commerce_service already resolves the tax destination from the event Customer's stored address and
 * then the event's PayoutPlatform when we send no address at all.
 *
 * @param registryAddress
 * @param defaultUSABillingAddress
 * @returns canShowGWResponse
 */
export const canShowGW = (registryAddress: GiftWrapAddress | null | undefined, defaultUSABillingAddress?: GiftWrapAddress | null): canShowGWResponse => {
  const registryCountryCode = getCountryCode(registryAddress);
  if (registryCountryCode) {
    if (isUnshippableUSAddress(registryCountryCode, registryAddress)) {
      return {
        address: undefined,
        allow: false,
        type: EAddressType.US_NON_CONTINENTAL,
      };
    }

    return {
      address: isAddressValid(registryAddress) ? toShippingAddressInput(registryAddress) : undefined,
      allow: true,
      type: registryCountryCode.toUpperCase() === USA_COUNTRY_CODE ? EAddressType.US_REGISTRY : EAddressType.INTERNATIONAL_REGISTRY,
    };
  }

  const defaultCountryCode = getCountryCode(defaultUSABillingAddress);
  if (isAddressValid(defaultUSABillingAddress) && defaultCountryCode) {
    if (isUnshippableUSAddress(defaultCountryCode, defaultUSABillingAddress)) {
      return {
        address: undefined,
        allow: false,
        type: EAddressType.US_NON_CONTINENTAL,
      };
    }

    return {
      address: toShippingAddressInput(defaultUSABillingAddress),
      allow: true,
      type: EAddressType.US_DEFAULT,
    };
  }

  return {
    address: undefined,
    allow: false,
    type: EAddressType.NO_ADDRESS,
  };
};
